import React, { useEffect, useState } from "react";
import API from "../../services/api";

const AssetLibraryModal = ({ open, onClose, onSelect }) => {
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get("/designer/assets/?type=image");
      setAssets(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load assets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAssets();
    }
  }, [open]);

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setError("");

      const formData = new FormData();
      formData.append("title", title || file.name);
      formData.append("file", file);
      formData.append("type", "image");

      await API.post("/designer/assets/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setTitle("");
      setFile(null);
      await fetchAssets();
    } catch (err) {
      console.error(err);
      setError("Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetId) => {
    const confirmed = window.confirm("Delete this image from library?");
    if (!confirmed) return;

    try {
      setDeletingId(assetId);
      setError("");
      await API.delete(`/designer/assets/${assetId}/`);
      setAssets((prev) => prev.filter((asset) => asset.id !== assetId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete image.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1000px, 95vw)",
          maxHeight: "90vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: 14,
          padding: 20,
          boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>Image Library</h3>
          <button onClick={onClose}>Close</button>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            background: "#f8fafc",
          }}
        >
          <h4 style={{ marginTop: 0 }}>Upload New Image</h4>

          <div style={{ display: "grid", gap: 10 }}>
            <input
              type="text"
              placeholder="Optional title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
              }}
            />

            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              style={{
                width: 180,
                padding: "9px 12px",
                border: "none",
                borderRadius: 8,
                background: !file || uploading ? "#94a3b8" : "#2563eb",
                color: "#fff",
                cursor: !file || uploading ? "not-allowed" : "pointer",
              }}
            >
              {uploading ? "Uploading..." : "Upload Image"}
            </button>
          </div>
        </div>

        {error && <p style={{ color: "red" }}>{error}</p>}
        {loading && <p>Loading assets...</p>}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 16,
          }}
        >
          {assets.map((asset) => (
            <div
              key={asset.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f8fafc",
                  padding: 8,
                }}
              >
                <img
                  src={asset.file_url}
                  alt={asset.title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              <div style={{ padding: 10 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    marginBottom: 8,
                    wordBreak: "break-word",
                  }}
                >
                  {asset.title}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <button
                    onClick={() => {
                      onSelect(asset);
                      onClose();
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      border: "none",
                      borderRadius: 8,
                      background: "#16a34a",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Select
                  </button>

                  <button
                    onClick={() => handleDelete(asset.id)}
                    disabled={deletingId === asset.id}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      border: "none",
                      borderRadius: 8,
                      background: deletingId === asset.id ? "#94a3b8" : "#dc2626",
                      color: "#fff",
                      cursor: deletingId === asset.id ? "not-allowed" : "pointer",
                    }}
                  >
                    {deletingId === asset.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && assets.length === 0 && (
          <p style={{ color: "#6b7280", marginTop: 16 }}>
            No images in library yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default AssetLibraryModal;